/* eslint-disable lingui/no-unlocalized-strings -- caminhos HTTP, chaves e valores técnicos do fio, não texto de UI */
import type { I18n, MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { z } from 'zod';

import { isValidCnpj } from '@/shared/format/cnpj';
import { isValidCpf } from '@/shared/format/cpf';
import { parseInstant } from '@/shared/format/date';
import type { Cents } from '@/shared/format/money';
import { creditApi } from '@/shared/http';

import { cleanDocument } from './credit-note.list-actions';

// Unidade 10D (docs/execucao/CREDIT-NOTE-PAGAMENTO-RELATORIO.md): a aba Dados bancários do roteiro de
// liquidação (Tabs/BankAccount/**) e o "Concluir revisão de pagamento" nos dois modos. O fio, os corpos e as
// regras que os componentes do legado calculam, com arquivo:linha ao lado de cada peça.

const BASE = '/CreditNote';
type Values = Record<string, unknown>;
const isRecord = (value: unknown): value is Values => value !== null && typeof value === 'object' && !Array.isArray(value);

// ─── fio ────────────────────────────────────────────────────────────────────

export interface PaymentReviewBody {
  liquidationScheduleItems: { liquidationScheduleId: unknown; liquidationSchedule: Values }[];
}

/** `postApproveRevisionAsync` (LiquidationSchedule.ts:91-101). */
export async function paymentRevisionLiquidationSchedule(id: string, body: PaymentReviewBody) {
  return creditApi.post(`${BASE}/${id}/paymentRevisionLiquidationSchedule`, body);
}

/** `deleteLiquidationScheduleAsync` (LiquidationSchedule.ts:103-112). */
export async function deleteLiquidationScheduleItem(id: string, itemId: string) {
  return creditApi.delete(`${BASE}/${id}/LiquidationSchedule/${itemId}`);
}

/** `DonePaymentRevision` (creditNoteReadModel.ts:178-181): as contas completas ou `null`. */
export interface DonePaymentRevisionBody {
  bankAccount: Values | null;
  changeBankAccount: Values | null;
}

// ─── Concluir revisão de pagamento em roteiro ───────────────────────────────

/** `LiquidationScheduleRequestModel` (LiquidationScheduleRequestModel.ts:17-25), na ordem da classe. */
const SCHEDULE_REQUEST_KEYS = ['amountInCents', 'informedDueDate', 'pixQrCode', 'bankSlip', 'pixKey', 'transfer', 'description'] as const;
const REVIEW_STATUSES = ['Failed', 'Disapproved'];

/** `paymentRevisionLiquidationSchedule` (creditNoteActions.tsx:1517-1528): o roteiro do formulário, só os vinculados e reprovados. */
export function paymentReviewItems(schedule: readonly unknown[] | null | undefined): Values[] {
  return (schedule ?? []).filter(isRecord).filter((item) => item['linkStatus'] === 'Linked' && REVIEW_STATUSES.includes(String(item['status'])));
}

/** `mapPaymentReviewItems` (LiquidationScheduleRequestModel.ts:41-53): `toDataModel` de cada item com o id ao lado. */
export function paymentReviewBody(items: readonly Values[]): PaymentReviewBody {
  return {
    liquidationScheduleItems: items.map((item) => {
      const liquidationSchedule: Values = {};
      for (const key of SCHEDULE_REQUEST_KEYS) if (key in item) liquidationSchedule[key] = item[key];
      return { liquidationScheduleId: item['id'], liquidationSchedule };
    }),
  };
}

export const amountOf = (item: Values): Cents => {
  const value = item['amountInCents'];
  return typeof value === 'number' && Number.isFinite(value) ? Math.round(value) : 0;
};

// ─── quem edita o quê ───────────────────────────────────────────────────────

const FORM_EDIT_STATUSES = ['Draft', 'Revision', 'Disapproved'];

/**
 * `readOnly` do formulário (CreditNoteFormContainer.tsx:194-196) e as exceções da aba: os cartões do roteiro editam
 * em Revisão de Pagamento e Rascunho mesmo sem a permissão (LiquidationScheduleList.tsx:22-24); "Selecionar contas"
 * e "Inserir pagamento" seguem o `readOnly` (SelectAccountContainer.tsx:126, LiquidationScheduleHeader.tsx:16).
 */
export function bankTabGates(status: string, canUpdate: boolean) {
  const readOnly = !FORM_EDIT_STATUSES.includes(status) || !canUpdate;
  return {
    readOnly,
    accountsEditable: !readOnly,
    canAddPayment: !readOnly,
    cardsEditable: status === 'PaymentRevision' || status === 'Draft' || !readOnly,
    paymentRevision: status === 'PaymentRevision',
    /** `isNotPaymentRevision` (LiquidationScheduleContainer.tsx:29-30): fora da revisão, o item gravado sai também no servidor. */
    deletesOnServer: status !== 'PaymentRevision',
  };
}
export type BankTabGates = ReturnType<typeof bankTabGates>;

/** Em Revisão de Pagamento os reprovados vêm primeiro (LiquidationScheduleList.tsx:26-32); o índice é o da lista. */
export function displayOrder(schedule: readonly unknown[], paymentRevision: boolean): number[] {
  const indexes = schedule.map((_, index) => index);
  if (!paymentRevision) return indexes;
  const failed = (index: number) => (isRecord(schedule[index]) && REVIEW_STATUSES.includes(String(schedule[index]['status'])) ? 1 : 0);
  return indexes.sort((a, b) => failed(b) - failed(a));
}

// ─── cartões (mapDataToCardValue, types/utils/index.tsx:12-177) ────────────

export type CardKind = 'bankSlip' | 'pix' | 'pixQrCode' | 'transfer';
export type StatusTone = 'ok' | 'err' | 'warn' | 'neutral';

/** `getTransferStatusInfo` (types/utils/index.tsx:257-279): Reprovado (`Disapproved`) cai em "Novo", como lá. */
export function scheduleStatusInfo(status: unknown): { readonly label: MessageDescriptor; readonly tone: StatusTone; readonly paid: boolean } {
  switch (status) {
    case 'Finished':
      return { label: msg`Pago`, tone: 'ok', paid: true };
    case 'Failed':
      return { label: msg`Rejeitado`, tone: 'err', paid: false };
    case 'Pendding':
      return { label: msg`Pendente`, tone: 'warn', paid: false };
    default:
      return { label: msg`Novo`, tone: 'neutral', paid: false };
  }
}

const text = (value: unknown): string => (typeof value === 'string' ? value : typeof value === 'number' ? String(value) : '');

export interface ScheduleCard {
  readonly kind: CardKind;
  /** O título: o `typeDisplay` do item com o sufixo, ou o padrão sem ele. */
  readonly header: MessageDescriptor;
  readonly paymentTitle: MessageDescriptor | null;
  readonly paymentValue: string;
  readonly bankLine: string | null;
  readonly agency: string;
  readonly account: string;
  readonly beneficiaryName: string;
  readonly beneficiaryDocument: string;
  readonly description: string;
  readonly dueDate: string | null;
  readonly informedDueDate: string | null;
  readonly error: string;
}

/** `bankNameLabel`/`bankCode` do `AccountDetails` (Card/AccountDetails.tsx:76-82). */
function bankLine(display: unknown, code: unknown): string | null {
  const name = text(display);
  if (name && /^\d/.test(name)) return name;
  if (code !== undefined && code !== null && name) return `${text(code)} - ${name}`;
  return name || null;
}

/** O cartão de um item do roteiro (types/utils/index.tsx:39-142). `null` quando o item não tem forma conhecida. */
export function scheduleCard(item: Values): ScheduleCard | null {
  const typeDisplay = text(item['typeDisplay']);
  const retrieved = isRecord(item['retrievedData']) ? item['retrievedData'] : {};
  const beneficiary = isRecord(retrieved['beneficiary']) ? retrieved['beneficiary'] : {};
  const bankslipData = isRecord(retrieved['bankslipData']) ? retrieved['bankslipData'] : {};
  const base = {
    description: text(item['description']),
    informedDueDate: text(item['informedDueDate']) || null,
    error: text(item['erroMessage']),
    beneficiaryName: text(beneficiary['name']),
    beneficiaryDocument: text(beneficiary['registrationNumber']),
    dueDate: null,
    bankLine: null,
    agency: '',
    account: '',
  };
  const qr = item['pixQrCode'];
  if (isRecord(qr)) return { ...base, kind: 'pixQrCode', header: typeDisplay ? msg`${typeDisplay} (QR Code)` : msg`QR Code`, paymentTitle: msg`Pix QR Code`, paymentValue: text(qr['qrCode']) };
  const slip = item['bankSlip'];
  if (isRecord(slip)) return { ...base, kind: 'bankSlip', header: typeDisplay ? msg`${typeDisplay} (Boleto)` : msg`Boleto`, paymentTitle: msg`Boleto`, paymentValue: text(slip['barcodeOrDigitableLine']), dueDate: text(bankslipData['dueDate']) || null };
  const pix = item['pixKey'];
  const transfer = isRecord(item['transfer']) ? item['transfer'] : null;
  const to = transfer && isRecord(transfer['to']) ? transfer['to'] : {};
  const transferCard = (header: MessageDescriptor): ScheduleCard => ({
    ...base,
    kind: 'transfer',
    header,
    paymentTitle: null,
    paymentValue: '',
    bankLine: bankLine(to['bankCodeDisplay'], to['bankCode']),
    agency: text(to['agency']),
    account: text(to['accountNumber']),
    beneficiaryName: text(to['name']),
    beneficiaryDocument: text(to['registrationNumber']),
  });
  if (isRecord(pix)) {
    if (Number(pix['pixKeyType']) === 6 && transfer) return transferCard(typeDisplay ? msg`${typeDisplay} (Pix)` : msg`Pix (Agência e Conta)`);
    return { ...base, kind: 'pix', header: typeDisplay ? msg`${typeDisplay} (Pix)` : msg`Pix`, paymentTitle: msg`Chave Pix`, paymentValue: text(pix['key']) };
  }
  if (transfer) {
    // `isPixAgencyAndAccount` (types/utils/index.tsx:107-111).
    const pixAgency = transfer['type'] === 2 || transfer['typeDisplay'] === 'Pix' || transfer['typeDisplay'] === 'Pix Agência e Conta' || to['type'] === 'Pix';
    return transferCard(pixAgency ? msg`Pix (Agência e Conta)` : typeDisplay ? msg`${typeDisplay} (TED)` : msg`TED`);
  }
  return null;
}

// ─── contas transitória e troco (SelectAccount/**) ─────────────────────────

/** `RelatedBankAccountType` (relatedBankAccountContext.tsx:14-23): a conta do cadastro e o rótulo do select. */
export interface RelatedAccount {
  readonly value: string;
  readonly account: Values;
}

const digits = (value: unknown) => text(value).replace(/\D/g, '');

/**
 * `useRelatedBankAccountData` (relatedBankAccountContext.tsx:56-99): as contas do cadastro da pessoa e a conta
 * transitória da operação, quando ela não está entre elas (mesmo banco, agência e conta), com o id da operação.
 */
export function relatedAccounts(personAccounts: readonly Values[], transitory: unknown, transitoryId: unknown): RelatedAccount[] {
  const merged = [...personAccounts];
  const transitoryAccount = isRecord(transitory) ? transitory : null;
  const resolvedId = text(transitoryId) || text(transitoryAccount?.['id']);
  if (transitoryAccount && transitoryAccount['bankCode'] && transitoryAccount['account'] && transitoryAccount['operationTypeValue'] && resolvedId) {
    const exists = merged.some(
      (item) => text(item['bankCode']) === text(transitoryAccount['bankCode']) && digits(item['agency']) === digits(transitoryAccount['agency']) && digits(item['account']) === digits(transitoryAccount['account']),
    );
    if (!exists) merged.push({ ...transitoryAccount, id: resolvedId, bankCodeDisplay: transitoryAccount['bankCodeDisplay'] ?? 'UY3 SCD S/A' });
  }
  return merged.map((account) => ({ value: text(account['id']), account }));
}

/** Conta transitória: TED no banco 457 (SelectAccountForm.tsx:40-44). A comparação é com o texto `'457'`, como lá. */
export const isTransitoryAccount = (account: Values) => account['operationTypeValue'] === 'Transfer' && account['bankCode'] === '457';

export interface AccountChoice {
  /** O id da conta transitória escolhida, `''` com "Abrir nova conta"/"Não abrir conta" (AccountSelector.tsx:22-30). */
  readonly transitoryBankAccountId: string | null | undefined;
  readonly transitoryBankAccountOpenAccount: boolean | null | undefined;
  readonly changeBankAccountId: string | null | undefined;
}

/** "Abrir nova conta" e "Não abrir conta" (SelectAccountForm.tsx:51-62), antes das contas 457. */
export const OPEN_ACCOUNT_OPTIONS: readonly { readonly value: boolean; readonly label: MessageDescriptor }[] = [
  { value: true, label: msg`Abrir nova conta` },
  { value: false, label: msg`Não abrir conta` },
];

/** As contas que os cartões mostram (`loadBankAccounts`/`handleConfirm`, SelectAccountContainer.tsx:41-77): as das duas escolhas. */
export function chosenAccounts(accounts: readonly RelatedAccount[], choice: AccountChoice): RelatedAccount[] {
  return accounts.filter((item) => (!!choice.transitoryBankAccountId && item.value === choice.transitoryBankAccountId) || (!!choice.changeBankAccountId && item.value === choice.changeBankAccountId));
}

// ─── o item do roteiro (LiquidationScheduleContainer + LiquidationScheduleForm) ─

export type TransactionType = 'transfer' | 'pixKey' | 'pixQrCode' | 'bankSlip';

/** "Tipo de transação" (LiquidationScheduleForm.tsx:26-31). */
export const TRANSACTION_TYPES: readonly { readonly value: TransactionType; readonly label: MessageDescriptor }[] = [
  { value: 'transfer', label: msg`Transferência` },
  { value: 'pixKey', label: msg`Chave Pix` },
  { value: 'pixQrCode', label: msg`Pix Copia e Cola` },
  { value: 'bankSlip', label: msg`Boleto bancário` },
];

/**
 * `pixKeyTypeOptions` (utils/enums/index.ts:141-148). `wire` é o `typeDisplay` que o legado grava (o rótulo do
 * select em pt-BR), que vai no corpo como texto do fio.
 */
export const SCHEDULE_PIX_KEY_TYPES: readonly { readonly value: string; readonly number: number; readonly wire: string; readonly label: MessageDescriptor }[] = [
  { value: 'NaturalRegistrationNumber', number: 1, wire: 'CPF', label: msg`CPF` },
  { value: 'LegalRegistrationNumber', number: 2, wire: 'CNPJ', label: msg`CNPJ` },
  { value: 'Phone', number: 3, wire: 'Telefone', label: msg`Telefone` },
  { value: 'Email', number: 4, wire: 'Email', label: msg`Email` },
  { value: 'Automatic', number: 5, wire: 'Aleatória', label: msg`Aleatória` },
  { value: 'AgencyAndAccount', number: 6, wire: 'Agência e Conta', label: msg`Agência e Conta` },
];
export const PIX_AGENCY = 'AgencyAndAccount';

/** `accountTypeOptions` (BankAccountFilters/constants.ts:23-33). */
export const SCHEDULE_ACCOUNT_TYPES: readonly { readonly value: string; readonly label: MessageDescriptor }[] = [
  { value: 'NaturalSimpleAccount', label: msg`Conta Simples de Pessoa Física` },
  { value: 'NaturalCheckingAccount', label: msg`Conta Corrente de Pessoa Física` },
  { value: 'LegalCheckingAccount', label: msg`Conta Corrente de Pessoa Jurídica` },
  { value: 'PublicEntities', label: msg`Entidades Públicas` },
  { value: 'FinancialInstitutionsDeposits', label: msg`Depósitos Instituições Financeiras` },
  { value: 'NaturalSavingsAccount', label: msg`Poupança de Pessoa Física` },
  { value: 'LegalSavingsAccount', label: msg`Poupança de Pessoa Jurídica` },
  { value: 'LotteryDeposits', label: msg`Depósitos Lotéricos` },
  { value: 'EscrowAccount', label: msg`Conta Escrow` },
];
/** `ERelatedBankAccountType` (utils/enums/index.ts:121-132): o `oneOf` aceita também a poupança imobiliária. */
const ACCOUNT_TYPE_VALUES = [...SCHEDULE_ACCOUNT_TYPES.map((option) => option.value), 'RealEstateSavingsAccount'];

const pixKeyTypeName = (value: unknown): string | null => {
  if (typeof value === 'number') return SCHEDULE_PIX_KEY_TYPES.find((option) => option.number === value)?.value ?? null;
  return typeof value === 'string' && value !== '' ? value : null;
};

/** `cleanPixKeyValue` (helpers/formats/PixKey.ts): CPF e CNPJ só sem separadores (CNPJ alfanumérico); telefone, dígitos. */
export function cleanPixKey(value: string, type: string | null): string {
  if (!value) return '';
  switch (type) {
    case 'NaturalRegistrationNumber':
    case 'LegalRegistrationNumber':
      return cleanDocument(value);
    case 'Phone':
      return value.replace(/\D/g, '');
    case 'Email':
      return value.replace(/\s/g, '').toLowerCase();
    case 'Automatic':
    case 'AgencyAndAccount':
      return value.replace(/\s/g, '');
    default:
      return value;
  }
}

export interface TransferTo {
  name: string | null;
  registrationNumber: string | null;
  bankCode: string | null;
  bankCodeDisplay: string | null;
  bankIspb: number | null;
  agency: string | null;
  accountNumber: string | null;
  accountType: string | null;
}

export interface ScheduleFormValues {
  description: string | null;
  transactionType: TransactionType | null;
  pixQrCode: { qrCode: string | null } | null;
  bankSlip: { barcodeOrDigitableLine: string | null } | null;
  pixKey: { key: string | null; pixKeyType: string | null; typeDisplay: string | null } | null;
  transfer: { type?: unknown; typeDisplay?: unknown; to: TransferTo } | null;
  amountInCents: Cents | null;
  /** Dia civil (`AAAA-MM-DD`) do `DatePickerFormField`. */
  informedDueDate: string | null;
}

/** `getTransactionType` (types/utils/index.tsx:179-192) pelo `paymentType` do `GET`. */
export function transactionTypeOf(item: Values | undefined): TransactionType | null {
  if (!item) return null;
  if (typeof item['transactionType'] === 'string' && TRANSACTION_TYPES.some((option) => option.value === item['transactionType'])) return item['transactionType'] as TransactionType;
  switch (item['paymentType']) {
    case 'QrCode':
      return 'pixQrCode';
    case 'Bankslip':
      return 'bankSlip';
    case 'Pix':
      return 'pixKey';
    case 'BankTransfer':
      return 'transfer';
    default:
      return null;
  }
}

const civilOf = (value: unknown): string | null => {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}/.test(value)) return null;
  if (value.length === 10 || !/(Z|[+-]\d{2}:?\d{2})$/.test(value)) return value.slice(0, 10);
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value.slice(0, 10);
  return new Date(date.getTime() - 3 * 3_600_000).toISOString().slice(0, 10);
};

const emptyTo = (): TransferTo => ({ name: '', registrationNumber: '', bankCode: '', bankCodeDisplay: '', bankIspb: null, agency: '', accountNumber: '', accountType: null });

/** O `transfer` que o tipo de chave "Agência e Conta" cria (LiquidationScheduleContainer.tsx:156-171). */
export const pixAgencyTransfer = (): NonNullable<ScheduleFormValues['transfer']> => ({ type: 2, typeDisplay: 'Pix', to: emptyTo() });

const textOrNull = (value: unknown): string | null => (typeof value === 'string' ? value : typeof value === 'number' ? String(value) : null);

/**
 * Os valores ao abrir (`defaultValues`, LiquidationScheduleContainer.tsx:122-127, e os efeitos do formulário,
 * LiquidationScheduleForm.tsx:58-104): o item em edição com a chave Pix pelo nome do tipo e sem máscara, ou o vazio.
 */
export function scheduleFormDefaults(item: Values | undefined): ScheduleFormValues {
  if (!item) {
    // `defaultValuesLiquidationSchema` (LiquidationSchedules.ts:205-217).
    return { description: null, transactionType: null, pixQrCode: null, bankSlip: null, pixKey: { key: null, pixKeyType: null, typeDisplay: null }, transfer: null, amountInCents: 0, informedDueDate: null };
  }
  const pix = isRecord(item['pixKey']) ? item['pixKey'] : null;
  const pixType = pixKeyTypeName(pix?.['pixKeyType']);
  const transfer = isRecord(item['transfer']) ? item['transfer'] : null;
  const to = transfer && isRecord(transfer['to']) ? transfer['to'] : null;
  const qr = isRecord(item['pixQrCode']) ? item['pixQrCode'] : null;
  const slip = isRecord(item['bankSlip']) ? item['bankSlip'] : null;
  return {
    description: textOrNull(item['description']),
    transactionType: transactionTypeOf(item),
    pixQrCode: qr ? { qrCode: textOrNull(qr['qrCode']) } : null,
    bankSlip: slip ? { barcodeOrDigitableLine: textOrNull(slip['barcodeOrDigitableLine']) } : null,
    // LiquidationScheduleForm.tsx:97-104: com "Chave Pix", o rótulo do tipo escolhido regrava o `typeDisplay` ao abrir.
    pixKey: pix ? { key: cleanPixKey(text(pix['key']), pixType), pixKeyType: pixType, typeDisplay: (transactionTypeOf(item) === 'pixKey' ? SCHEDULE_PIX_KEY_TYPES.find((option) => option.value === pixType)?.wire : undefined) ?? textOrNull(pix['typeDisplay']) } : null,
    transfer: transfer
      ? {
          ...transfer,
          to: to
            ? {
                name: textOrNull(to['name']),
                registrationNumber: textOrNull(to['registrationNumber']),
                bankCode: textOrNull(to['bankCode']),
                bankCodeDisplay: textOrNull(to['bankCodeDisplay']),
                bankIspb: typeof to['bankIspb'] === 'number' ? to['bankIspb'] : null,
                agency: textOrNull(to['agency']),
                accountNumber: textOrNull(to['accountNumber']),
                accountType: textOrNull(to['accountType']),
              }
            : emptyTo(),
        }
      : null,
    amountInCents: typeof item['amountInCents'] === 'number' ? item['amountInCents'] : null,
    informedDueDate: civilOf(item['informedDueDate']),
  };
}

/** `onChangeField` de `transactionType` (LiquidationScheduleContainer.tsx:133-152): o que cada tipo limpa. */
export function clearedForTransaction(type: string | null): Partial<ScheduleFormValues> {
  switch (type) {
    case 'transfer':
      return { pixQrCode: null, pixKey: null, bankSlip: null };
    case 'bankSlip':
      return { pixQrCode: null, pixKey: null, transfer: null };
    case 'pixQrCode':
      return { bankSlip: null, pixKey: null, transfer: null };
    case 'pixKey':
      return { bankSlip: null, pixQrCode: null };
    default:
      return {};
  }
}

const onlyDigits = (value: unknown) => text(value).replace(/\D/g, '');

/** `digitableLineToBarcode` + `getBankSlipComparableKey` (helpers/formats/BarCodeFormat.ts:3-16). */
export function bankSlipComparableKey(value: unknown): string {
  const line = onlyDigits(value);
  if (line.length !== 47) return line;
  return line.slice(0, 4) + (line[32] ?? '') + line.slice(33, 47) + line.slice(4, 9) + line.slice(10, 20) + line.slice(21, 31);
}

/** O boleto repetido no roteiro (LiquidationScheduleContainer.tsx:66-80), sem contar o item em edição. */
export function isDuplicateBankSlip(schedule: readonly unknown[], values: ScheduleFormValues, editing: number | undefined): boolean {
  if (values.transactionType !== 'bankSlip') return false;
  const key = bankSlipComparableKey(values.bankSlip?.barcodeOrDigitableLine);
  if (!key) return false;
  return schedule.some((item, index) => index !== editing && isRecord(item) && isRecord(item['bankSlip']) && bankSlipComparableKey(item['bankSlip']['barcodeOrDigitableLine']) === key);
}

const atSaoPauloMidnight = (civil: string) => new Date(`${civil}T00:00:00-03:00`).toISOString();
const instantOf = (value: unknown): string | null => (typeof value === 'string' ? (parseInstant(value)?.toISOString() ?? null) : null);

/**
 * O item que o "Confirmar" grava no formulário (`onSubmit`, LiquidationScheduleContainer.tsx:62-113) depois do cast
 * do yup: o item original por baixo (id, status e vínculo continuam), a chave Pix pelo número do tipo e sem
 * máscara, "Agência e Conta" sem `pixKey` e o `transfer` com tipo (Pix 2 ou TED 1) e rótulo.
 */
export function scheduleItemFromForm(values: ScheduleFormValues, original: Values | undefined): Values {
  const out: Values = { ...(original ?? {}), ...values };
  // `date()` do yup: o dia escolhido é meia-noite local; no JSON, o instante em UTC. Sem mudar o dia, fica o instante do
  // item (`new Date(informedDueDate)`, LiquidationScheduleContainer.tsx:122-127), com a hora que ele tinha.
  const kept = original && values.informedDueDate !== null && values.informedDueDate === civilOf(original['informedDueDate']) ? instantOf(original['informedDueDate']) : null;
  out['informedDueDate'] = values.informedDueDate ? (kept ?? atSaoPauloMidnight(values.informedDueDate)) : null;
  const pixType = values.pixKey?.pixKeyType ?? null;
  const pixAgency = pixType === PIX_AGENCY;
  if (values.pixKey && pixType) {
    out['pixKey'] = {
      ...values.pixKey,
      pixKeyType: SCHEDULE_PIX_KEY_TYPES.find((option) => option.value === pixType)?.number ?? pixType,
      key: values.pixKey.key ? cleanPixKey(values.pixKey.key, pixType) : values.pixKey.key,
    };
  }
  if (pixAgency) out['pixKey'] = null;
  if (values.transfer?.to) {
    out['transfer'] = {
      ...values.transfer,
      to: { ...values.transfer.to, registrationNumber: values.transfer.to.registrationNumber ? cleanDocument(values.transfer.to.registrationNumber) : values.transfer.to.registrationNumber },
      type: values.transfer.type ?? (pixAgency ? 2 : 1),
      typeDisplay: values.transfer.typeDisplay ?? (pixAgency ? 'Pix' : 'TED'),
    };
  }
  return out;
}

// ─── validação (liquidationItemSchema, schema/LiquidationSchedules.ts:6-192) ───

const blank = (value: unknown) => value === null || value === undefined || (typeof value === 'string' && value.trim() === '');

/** O documento do `cpfCnpjValidation`: CPF com 11 caracteres, CNPJ com 14 (alfanumérico com a flag). */
function validDocument(value: string): boolean {
  const raw = cleanDocument(value);
  return raw.length === 11 ? isValidCpf(raw) : raw.length === 14 && isValidCnpj(raw);
}

function pixKeyValid(pix: ScheduleFormValues['pixKey']): boolean {
  const type = pix?.pixKeyType ?? null;
  if (!type) return false;
  const key = pix?.key ?? '';
  if (!key) return false;
  const clean = cleanDocument(key);
  if (type === 'Phone' || type === 'NaturalRegistrationNumber') return clean.length === 11;
  if (type === 'LegalRegistrationNumber') return clean.length === 14;
  return true;
}

/**
 * O yup do drawer com as mensagens literais do legado; a primeira falha de cada campo, como o RHF mostra.
 * `kovr`: a data informada é obrigatória e não pode ser passada (`IS_KOVR`, :106-117).
 */
export function scheduleItemSchema(i18n: I18n, options: { readonly kovr: boolean; readonly today: string }) {
  return z.custom<ScheduleFormValues>().superRefine((values, context) => {
    const issue = (path: (string | number)[], message: MessageDescriptor) => context.addIssue({ code: 'custom', path, message: i18n._(message) });
    if (!values.transactionType) issue(['transactionType'], msg`Selecione o tipo de transação.`);
    const amount = values.amountInCents;
    if (amount === null) issue(['amountInCents'], msg`Campo é obrigatório.`);
    else if (!Number.isFinite(amount)) issue(['amountInCents'], msg`O valor precisa ser um número.`);
    else if (amount <= 0) issue(['amountInCents'], msg`O valor deve ser positivo.`);
    if (options.kovr) {
      if (!values.informedDueDate) issue(['informedDueDate'], msg`Data de vencimento informada é obrigatória.`);
      else if (values.informedDueDate < options.today) issue(['informedDueDate'], msg`A data de vencimento não pode ser no passado`);
    }
    if (values.transactionType === 'pixQrCode' && blank(values.pixQrCode?.qrCode)) issue(['pixQrCode', 'qrCode'], msg`O QR code: Precisa ser preenchido.`);
    if (values.transactionType === 'bankSlip') {
      const line = values.bankSlip?.barcodeOrDigitableLine ?? '';
      if (line === '') issue(['bankSlip', 'barcodeOrDigitableLine'], msg`A linha digitável ou código de barras: Precisa ser preenchido.`);
      else if (!/^\d+$/.test(line)) issue(['bankSlip', 'barcodeOrDigitableLine'], msg`Deve conter apenas números.`);
      else if (line.length !== 44 && line.length !== 47) issue(['bankSlip', 'barcodeOrDigitableLine'], msg`O código de barras ou linha digitável inválida.`);
    }
    const pixType = values.pixKey?.pixKeyType ?? null;
    const pixAgency = pixType === PIX_AGENCY;
    if (values.transactionType === 'pixKey' && !pixAgency && !(values.pixKey === null && values.transfer) && !pixKeyValid(values.pixKey)) {
      // O yup devolve um erro só, em `pixKey`, que nenhum campo mostra (:131-151); aqui ele fica no campo que falta.
      issue(['pixKey', pixType ? 'key' : 'pixKeyType'], msg`Dados da chave Pix inválidos`);
    }
    if (values.transactionType === 'transfer' || pixAgency) {
      const to: Partial<TransferTo> = values.transfer?.to ?? {};
      const at = (field: keyof TransferTo) => ['transfer', 'to', field];
      if (blank(to.name)) issue(at('name'), msg`O nome: Precisa ser preenchido.`);
      if (blank(to.registrationNumber)) issue(at('registrationNumber'), msg`CPF/CNPJ: Precisa ser preenchido.`);
      else if (!validDocument(to.registrationNumber ?? '')) issue(at('registrationNumber'), msg`CPF/CNPJ: inválido.`);
      if (blank(to.bankCode)) issue(at('bankCode'), msg`Código bancário: Precisa ser preenchido.`);
      const agency = to.agency ?? '';
      if (agency === '') issue(at('agency'), msg`A agência: Precisa ser preenchido.`);
      else if (!/^\d+$/.test(agency)) issue(at('agency'), msg`A agência deve conter apenas números.`);
      else if (agency.length > 4) issue(at('agency'), msg`A agência deve ter no máximo 4 caracteres.`);
      const account = to.accountNumber ?? '';
      if (account === '') issue(at('accountNumber'), msg`O número da conta: Precisa ser preenchido.`);
      else if (!/^[0-9-]+$/.test(account)) issue(at('accountNumber'), msg`Número da conta inválido.`);
      if (blank(to.accountType)) issue(at('accountType'), msg`O tipo de conta: Precisa ser preenchido.`);
      else if (!ACCOUNT_TYPE_VALUES.includes(to.accountType ?? '')) issue(at('accountType'), msg`Tipo de conta inválido.`);
    }
  });
}

// ─── Concluir revisão de pagamento fora do roteiro (PaymentRevisionFormContainer.tsx) ───

/** O valor da opção da conta criada no drawer (`formatBankAccountForOption(values, 'new')`, :160-187). */
export const NEW_ACCOUNT = 'new';

export interface PaymentRevisionValues {
  bankAccountId: string | null;
  changeBankAccountId: string | null;
  /** A conta digitada em "Adicionar dados bancários" para a liquidação e para o troco (`createdAccount.formValues`). */
  newBankAccount: Values | null;
  newChangeBankAccount: Values | null;
}

/**
 * Os valores ao abrir (PaymentRevisionFormContainer.tsx:193-196 + PaymentRevisionForm.tsx:31-36): a conta de liquidação
 * da operação; o troco nasce `'new'`, porque o efeito do formulário roda no mount com o `createdAccount` inicial (não
 * nulo) e o `type` indefinido. Sem conta digitada, o troco sai `null`, fora do CDC sempre, no CDC até alguém escolher.
 */
export function paymentRevisionDefaults(note: { readonly bankAccountId?: string | null | undefined }): PaymentRevisionValues {
  return { bankAccountId: note.bankAccountId ?? null, changeBankAccountId: NEW_ACCOUNT, newBankAccount: null, newChangeBankAccount: null };
}

/**
 * `onSubmit` (PaymentRevisionFormContainer.tsx:118-144): a conta completa do cadastro escolhida (do beneficiário no
 * CDC) ou a digitada, ou `null`. O troco `'new'` sem conta digitada vira `null` (o `find` não acha a opção nova).
 */
export function donePaymentRevisionBody(values: PaymentRevisionValues, accounts: { readonly bank: readonly Values[]; readonly change: readonly Values[] }): DonePaymentRevisionBody {
  const find = (list: readonly Values[], id: string | null) => (id ? (list.find((item) => item['id'] === id) ?? null) : null);
  return {
    bankAccount: find(accounts.bank, values.bankAccountId) ?? values.newBankAccount ?? null,
    changeBankAccount: find(accounts.change, values.changeBankAccountId) ?? values.newChangeBankAccount ?? null,
  };
}

/**
 * A conta digitada como o `createAccount` guarda (PaymentRevisionFormContainer.tsx:146-191): os valores do
 * `BankDetails` depois do cast do yup (`account` é `number()`, BankDetailsSchema.ts:50-57) e os campos do
 * `defaultValuesBankDetails` (:118-127) que o formulário não preenche.
 */
export function newAccountFromForm(values: Values): Values {
  const account = values['account'];
  const parsed = typeof account === 'string' && account.trim() !== '' && Number.isFinite(Number(account)) ? Number(account) : account === '' ? undefined : account;
  return { accountNumber: null, accountType: null, ...values, account: parsed };
}
